import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { zodValidator, fallback } from "@tanstack/zod-adapter";
import { z } from "zod";
import { Loader2, ChevronDown, Check } from "lucide-react";
import { ProductCard } from "@/components/ProductCard";
import { fetchProducts, formatPrice, type ShopifyProduct } from "@/lib/shopify";
import {
  getDiamondSpecs,
  SHAPE_OPTIONS,
  CLARITY_OPTIONS,
  COLOR_OPTIONS,
} from "@/lib/diamondSpecs";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Slider } from "@/components/ui/slider";

const shopSearchSchema = z.object({
  shape: fallback(z.string(), "").default(""),
  color: fallback(z.string(), "").default(""),
  clarity: fallback(z.string(), "").default(""),
  caratMin: fallback(z.number(), 0).optional(),
  caratMax: fallback(z.number(), 0).optional(),
  priceMin: fallback(z.number(), 0).optional(),
  priceMax: fallback(z.number(), 0).optional(),
});

export const Route = createFileRoute("/shop/")({
  validateSearch: zodValidator(shopSearchSchema),
  head: () => ({
    meta: [
      { title: "The Diamonds, HOWDEN" },
      {
        name: "description",
        content:
          "Certified lab-grown diamonds, offered loose by the carat. Solitaires and melee, IGI and GIA graded, ready for the setting of your choosing.",
      },
      { property: "og:title", content: "The Diamonds, HOWDEN" },
      {
        property: "og:description",
        content:
          "Certified lab-grown diamonds, offered loose by the carat. Solitaires and melee, IGI and GIA graded, ready for the setting of your choosing.",
      },
      { property: "og:url", content: "https://howdendiamonds.com/shop" },
    ],
    links: [{ rel: "canonical", href: "https://howdendiamonds.com/shop" }],
  }),
  component: ShopPage,
});

type Sort = "featured" | "price-asc" | "price-desc" | "carat-asc" | "carat-desc";

function ShopPage() {
  const { data: products = [], isLoading } = useQuery({
    queryKey: ["products", "all"],
    queryFn: () => fetchProducts(48),
  });

  const search = Route.useSearch();
  const [shape, setShape] = useState<string>(search.shape ?? "");
  const [color, setColor] = useState<string>(search.color ?? "");
  const [clarity, setClarity] = useState<string>(search.clarity ?? "");
  const [sort, setSort] = useState<Sort>("featured");

  const enriched = useMemo(
    () =>
      products.map((p) => ({
        product: p,
        specs: getDiamondSpecs(p),
        priceNum: parseFloat(p.node.priceRange.minVariantPrice.amount) || 0,
      })),
    [products],
  );

  const { caratBounds, priceBounds, currency } = useMemo(() => {
    const carats = enriched.map((e) => e.specs.carat).filter((c): c is number => c != null);
    const prices = enriched.map((e) => e.priceNum).filter((p) => p > 0);
    return {
      caratBounds: [
        carats.length ? Math.floor(Math.min(...carats) * 100) / 100 : 0,
        carats.length ? Math.ceil(Math.max(...carats) * 100) / 100 : 5,
      ] as [number, number],
      priceBounds: [
        prices.length ? Math.floor(Math.min(...prices)) : 0,
        prices.length ? Math.ceil(Math.max(...prices)) : 10000,
      ] as [number, number],
      currency:
        enriched[0]?.product.node.priceRange.minVariantPrice.currencyCode ?? "USD",
    };
  }, [enriched]);

  const [caratRange, setCaratRange] = useState<[number, number] | null>(
    search.caratMin != null && search.caratMax != null
      ? [search.caratMin, search.caratMax]
      : null,
  );
  const [priceRange, setPriceRange] = useState<[number, number] | null>(
    search.priceMin != null && search.priceMax != null
      ? [search.priceMin, search.priceMax]
      : null,
  );
  const activeCarat = caratRange ?? caratBounds;
  const activePrice = priceRange ?? priceBounds;

  const filtered = useMemo(() => {
    let list = enriched.filter(({ specs, priceNum }) => {
      if (shape && specs.shape !== shape) return false;
      if (color && specs.color !== color) return false;
      if (clarity && specs.clarity !== clarity) return false;
      if (caratRange && specs.carat != null) {
        if (specs.carat < caratRange[0] || specs.carat > caratRange[1]) return false;
      }
      if (priceRange) {
        if (priceNum < priceRange[0] || priceNum > priceRange[1]) return false;
      }
      return true;
    });

    list = [...list];
    switch (sort) {
      case "price-asc":
        list.sort((a, b) => a.priceNum - b.priceNum);
        break;
      case "price-desc":
        list.sort((a, b) => b.priceNum - a.priceNum);
        break;
      case "carat-asc":
        list.sort((a, b) => (a.specs.carat ?? 0) - (b.specs.carat ?? 0));
        break;
      case "carat-desc":
        list.sort((a, b) => (b.specs.carat ?? 0) - (a.specs.carat ?? 0));
        break;
    }
    return list.map((e) => e.product);
  }, [enriched, shape, color, clarity, sort, caratRange, priceRange]);

  const clearFilters = () => {
    setShape("");
    setColor("");
    setClarity("");
    setCaratRange(null);
    setPriceRange(null);
    setSort("featured");
  };
  const hasActiveFilters =
    !!(shape || color || clarity) || sort !== "featured" || !!caratRange || !!priceRange;

  return (
    <div className="mx-auto max-w-7xl px-6 py-20 lg:px-12 lg:py-28">
      <header className="mb-12 max-w-2xl">
        <p className="text-xs uppercase tracking-luxe text-muted-foreground">
          The Diamonds
        </p>
        <h1 className="mt-6 font-serif text-5xl font-light leading-tight lg:text-6xl">
          Considered, by the carat.
        </h1>
        <p className="mt-6 text-base leading-relaxed text-muted-foreground">
          Certified lab-grown diamonds, offered loose. From melee to
          solitaire, every stone is independently graded and ready to be
          set by the jeweller of your choosing.
        </p>
      </header>

      <div className="mb-12 flex flex-wrap items-center justify-between gap-4 border-b border-hairline pb-6">
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-xs uppercase tracking-luxe text-muted-foreground">
            Filter
          </span>
          <FilterSelect value={shape} onChange={setShape} label="Shape" options={SHAPE_OPTIONS} />
          <FilterSelect value={color} onChange={setColor} label="Color" options={COLOR_OPTIONS} />
          <FilterSelect value={clarity} onChange={setClarity} label="Clarity" options={CLARITY_OPTIONS} />
          <RangeFilter
            label="Carat"
            bounds={caratBounds}
            value={activeCarat}
            step={0.05}
            isActive={!!caratRange}
            format={(v) => `${v.toFixed(2)}ct`}
            onChange={(v) => setCaratRange(v)}
            onReset={() => setCaratRange(null)}
          />
          <RangeFilter
            label="Price"
            bounds={priceBounds}
            value={activePrice}
            step={Math.max(50, Math.round((priceBounds[1] - priceBounds[0]) / 100))}
            isActive={!!priceRange}
            format={(v) => formatPrice(v, currency)}
            onChange={(v) => setPriceRange(v)}
            onReset={() => setPriceRange(null)}
          />
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="text-xs uppercase tracking-luxe text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
            >
              Clear
            </button>
          )}
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs uppercase tracking-luxe text-muted-foreground">
            Sort
          </span>
          <FilterSelect
            value={sort}
            onChange={(v) => setSort(v as Sort)}
            label="Featured"
            options={[
              { value: "featured", label: "Featured" },
              { value: "price-asc", label: "Price · Low to High" },
              { value: "price-desc", label: "Price · High to Low" },
              { value: "carat-asc", label: "Carat · Low to High" },
              { value: "carat-desc", label: "Carat · High to Low" },
            ]}
            alwaysShowValue
          />
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-32">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" strokeWidth={1.25} />
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState hasProducts={products.length > 0} onClear={clearFilters} />
      ) : (
        <div className="grid gap-x-8 gap-y-16 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((p: ShopifyProduct) => (
            <ProductCard key={p.node.id} product={p} />
          ))}
        </div>
      )}
    </div>
  );
}

function RangeFilter({
  label,
  bounds,
  value,
  step,
  isActive,
  format,
  onChange,
  onReset,
}: {
  label: string;
  bounds: [number, number];
  value: [number, number];
  step: number;
  isActive: boolean;
  format: (n: number) => string;
  onChange: (v: [number, number]) => void;
  onReset: () => void;
}) {
  const [min, max] = bounds;
  const display = isActive ? `${format(value[0])} – ${format(value[1])}` : label;
  const disabled = min >= max;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className="inline-flex items-center justify-between gap-3 whitespace-nowrap border border-hairline bg-transparent px-4 py-2 text-xs uppercase tracking-luxe text-foreground transition-colors hover:border-foreground focus:border-foreground focus:outline-none data-[state=open]:border-foreground disabled:opacity-40"
        >
          <span>{display}</span>
          <ChevronDown className="h-3 w-3 text-muted-foreground" strokeWidth={1.5} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={6}
        className="w-72 rounded-none border border-hairline bg-background p-5 shadow-none"
      >
        <div className="mb-4 flex items-baseline justify-between">
          <span className="text-[10px] uppercase tracking-luxe text-muted-foreground">
            {label} range
          </span>
          {isActive && (
            <button
              onClick={onReset}
              className="text-[10px] uppercase tracking-luxe text-muted-foreground hover:text-foreground"
            >
              Reset
            </button>
          )}
        </div>
        <Slider
          min={min}
          max={max}
          step={step}
          value={[value[0], value[1]]}
          onValueChange={(v) => onChange([v[0], v[1]] as [number, number])}
          className="my-6"
        />
        <div className="flex items-center justify-between font-serif text-sm">
          <span>{format(value[0])}</span>
          <span className="text-muted-foreground">,</span>
          <span>{format(value[1])}</span>
        </div>
      </PopoverContent>
    </Popover>
  );
}

type Option = string | { value: string; label: string };

function FilterSelect({
  value,
  onChange,
  label,
  options,
  alwaysShowValue,
}: {
  value: string;
  onChange: (v: string) => void;
  label: string;
  options: Option[];
  alwaysShowValue?: boolean;
}) {
  const normalized = options.map((o) =>
    typeof o === "string" ? { value: o, label: o } : o,
  );
  const current = normalized.find((o) => o.value === value);
  const display = alwaysShowValue
    ? current?.label ?? normalized[0]?.label ?? label
    : current?.label ?? label;

  const items: { value: string; label: string }[] = alwaysShowValue
    ? normalized
    : [{ value: "", label: `All ${label}` }, ...normalized];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center justify-between gap-3 whitespace-nowrap border border-hairline bg-transparent px-4 py-2 text-xs uppercase tracking-luxe text-foreground transition-colors hover:border-foreground focus:border-foreground focus:outline-none data-[state=open]:border-foreground"
        >
          <span>{display}</span>
          <ChevronDown className="h-3 w-3 text-muted-foreground" strokeWidth={1.5} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        sideOffset={6}
        className="min-w-[var(--radix-dropdown-menu-trigger-width)] rounded-none border border-hairline bg-background p-0 shadow-none"
      >
        {items.map((o) => {
          const selected = o.value === value;
          return (
            <DropdownMenuItem
              key={o.value || "__all"}
              onSelect={() => onChange(o.value)}
              className="flex items-center justify-between gap-6 rounded-none px-4 py-2.5 text-xs uppercase tracking-luxe text-foreground/80 focus:bg-secondary focus:text-foreground data-[selected=true]:bg-secondary"
            >
              <span>{o.label}</span>
              {selected && <Check className="h-3 w-3" strokeWidth={1.5} />}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function EmptyState({
  hasProducts,
  onClear,
}: {
  hasProducts: boolean;
  onClear: () => void;
}) {
  return (
    <div className="flex flex-col items-center justify-center border border-dashed border-hairline px-6 py-32 text-center">
      <p className="text-xs uppercase tracking-luxe text-muted-foreground">
        {hasProducts ? "No matches" : "No products found"}
      </p>
      <h2 className="mt-4 max-w-md font-serif text-3xl font-light">
        {hasProducts ? "Try a different cut." : "The vault is empty."}
      </h2>
      {hasProducts && (
        <p className="mt-4 max-w-md text-sm text-muted-foreground">
          No stones in this section match your filters.
        </p>
      )}
      {hasProducts && (
        <button
          onClick={onClear}
          className="mt-8 border-b border-foreground pb-1 text-xs uppercase tracking-luxe"
        >
          Clear filters
        </button>
      )}
    </div>
  );
}
